import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ApiError, apiFetchJson } from "@rook/shared/lib/apiFetch";
import type { Environment } from "@rook/shared/lib/appContext";

import type {
  BasicAuthResponse,
  BasicAuthUpdate,
  NotificationWebhookResponse,
  WebhookResponse,
} from "./types";

const webhookQueryKey = (env: Environment) =>
  ["webhooks", "current", env] as const;

const notificationWebhookQueryKey = (env: Environment) =>
  ["webhooks", "notification", env] as const;

const basicAuthQueryKey = (env: Environment) =>
  ["webhooks", "basic-auth", env] as const;

/**
 * Default shape returned when the upstream answers 404 — "no
 * webhook configured yet for this env". Treating 404 as a success
 * branch (returning a `status: 0` payload) keeps the view as a
 * single happy path: it always renders the form, it just renders
 * it empty when there's nothing configured.
 *
 * Mirrors the legacy server action's 404 swallow in
 * refactor-next-stable's `src/modules/settings/server/getWebhook.ts`.
 */
const emptyWebhook = (): WebhookResponse => ({
  status: 0,
  client_id: -1,
  client_uuid: "",
  endpoint: "",
  data_type: "",
  usage: "",
  created_at: "",
  updated_at: "",
});

/**
 * Fetch the current webhook config for the given env. Goes through
 * the BFF `proxy-clients` Lambda → `rook_clients_api GET /client/webhooks`.
 * Translates upstream 404 (no config) into an empty-shape success
 * so the consumer never has to handle the not-found branch.
 */
export const useWebhook = (env: Environment) =>
  useQuery({
    queryKey: webhookQueryKey(env),
    queryFn: async () => {
      try {
        return await apiFetchJson<WebhookResponse>(
          `/api/proxy/clients/${env}/client/webhooks`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return emptyWebhook();
        }
        throw err;
      }
    },
  });

/**
 * Update the webhook endpoint for the given env. PUT
 * /client/webhooks with `{endpoint}`. On success, invalidates the
 * webhook query so the next read picks up the new endpoint +
 * the refreshed `updated_at`.
 */
export const useUpdateWebhook = (env: Environment) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (endpoint: string) =>
      apiFetchJson<{ ok?: boolean }>(
        `/api/proxy/clients/${env}/client/webhooks`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint }),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookQueryKey(env) });
    },
  });
};

/**
 * Fetch the current notification webhook URL for the given env.
 * Goes through `proxy-clients` → `GET /customize/webhook_notification`.
 * Translates upstream 404 (never configured) into a `{ notification_
 * webhook: "" }` so the consumer renders the form blank instead of
 * needing a not-found branch.
 */
export const useNotificationWebhook = (env: Environment) =>
  useQuery({
    queryKey: notificationWebhookQueryKey(env),
    queryFn: async () => {
      try {
        return await apiFetchJson<NotificationWebhookResponse>(
          `/api/proxy/clients/${env}/customize/webhook_notification`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { notification_webhook: "" };
        }
        throw err;
      }
    },
  });

/**
 * Update the notification webhook URL for the given env. PUT
 * `/customize/webhook_notification` with `{ notification_webhook }`.
 * An empty string clears the configured URL (CPF-FR-060: field is
 * optional and removable).
 */
export const useUpdateNotificationWebhook = (env: Environment) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationWebhook: string) =>
      apiFetchJson<{ ok?: boolean }>(
        `/api/proxy/clients/${env}/customize/webhook_notification`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notification_webhook: notificationWebhook }),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationWebhookQueryKey(env),
      });
    },
  });
};

/**
 * Fetch the current Basic Auth state for the data webhook. Backed by
 * the TWO split endpoints on rook_clients_api (CPF-FR-052):
 *
 *   `GET /customize/credentials_basic_auth` → { username, password? }
 *   `GET /customize/status_basic_auth`      → { status_basic_auth: bool }
 *
 * The v3-only "unified /customize/security" path that this hook
 * used to call DOES NOT EXIST on the backend — API Gateway returned
 * the "Missing Authentication Token" SigV4 error on every load.
 * Diagnosed 2026-05-23 (see types.ts BasicAuthResponse comment).
 *
 * Fetches both in parallel and composes the legacy `{ username,
 * password, enabled }` shape. 404 on either endpoint = "not
 * configured" → return empty shape so the form renders without an
 * extra branch.
 */
const creds404OrThrow = async (
  fetcher: () => Promise<{ username: string; password?: string }>,
): Promise<{ username: string; password?: string }> => {
  try {
    return await fetcher();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { username: "" };
    }
    throw err;
  }
};

const status404OrThrow = async (
  fetcher: () => Promise<{ status_basic_auth: boolean }>,
): Promise<{ status_basic_auth: boolean }> => {
  try {
    return await fetcher();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { status_basic_auth: false };
    }
    throw err;
  }
};

export const useBasicAuth = (env: Environment) =>
  useQuery({
    queryKey: basicAuthQueryKey(env),
    queryFn: async (): Promise<BasicAuthResponse> => {
      const [creds, status] = await Promise.all([
        creds404OrThrow(() =>
          apiFetchJson<{ username: string; password?: string }>(
            `/api/proxy/clients/${env}/customize/credentials_basic_auth`,
          ),
        ),
        status404OrThrow(() =>
          apiFetchJson<{ status_basic_auth: boolean }>(
            `/api/proxy/clients/${env}/customize/status_basic_auth`,
          ),
        ),
      ]);
      return {
        username: creds.username,
        password: creds.password,
        enabled: Boolean(status.status_basic_auth),
      };
    },
  });

/**
 * Update Basic Auth. Fans out to one or two backend PUTs per
 * CPF-UIR-084:
 *   - enable: PUT /customize/credentials_basic_auth (user+pass)
 *             THEN PUT /customize/status_basic_auth (status: true)
 *             — the only branch where a password rotation happens.
 *   - disable: PUT /customize/status_basic_auth (status: false)
 *             — creds untouched; backend keeps the username for the
 *             next enable.
 *
 * Sequential for the enable branch so creds land before the status
 * flips — avoids a delivery window where the backend reads
 * "enabled=true, password=<old>". For disable, single call.
 *
 * Invalidates the basic-auth + nav-status keys on success so the
 * sidebar LED + Logs + Simulator surfaces pick up the new state.
 */
export const useUpdateBasicAuth = (env: Environment) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (update: BasicAuthUpdate) => {
      if (update.enabled) {
        if (!update.username || !update.password) {
          throw new Error(
            "Username and password are both required to enable Basic Auth.",
          );
        }
        await apiFetchJson<{ ok?: boolean }>(
          `/api/proxy/clients/${env}/customize/credentials_basic_auth`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              username: update.username,
              password: update.password,
            }),
          },
        );
      }
      await apiFetchJson<{ ok?: boolean }>(
        `/api/proxy/clients/${env}/customize/status_basic_auth`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status_basic_auth: update.enabled }),
        },
      );
      return { ok: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: basicAuthQueryKey(env) });
    },
  });
};
